/**
 * PriceLadder (spec §4) — the market ladder.
 *
 * A horizontal graduated strip: lowest → p25 → median → p75 → highest as a
 * stepped band (box-plot idiom rendered friendly), the median the boldest
 * tick. The asking price is a labelled marker with a leader line; the marker
 * MOVES along the ladder as the price input changes — pure client maths over
 * the already-fetched distribution, no round trip.
 *
 * Hand-rolled SVG (spec §9: no charting library, ≤ ~200 lines). RTL mirrors
 * the ruler (low right / high left) via `rulerX`; numerals stay Western.
 */

import { useId } from 'react'
import type { SubjectDistribution } from '@/types/marketPricing'
import { formatMoney } from './pricingIntelligence'
import { priceBriefT, type PriceBriefLocale } from './copy'
import { rulerGeometry, rulerX, type RulerDirection } from './priceScale'

const WIDTH = 600
const HEIGHT = 140
const PAD = 46
const BAND_Y = 56
const BAND_H = 14

interface PriceLadderProps {
  distribution: SubjectDistribution
  /** Asking price on the ruler's normalized (USD) scale, or null when unset. */
  price: number | null
  currency: string
  dir: RulerDirection
  locale: PriceBriefLocale
}

interface Tick {
  key: string
  value: number
  label: string
  major: boolean
}

export function PriceLadder({ distribution, price, currency, dir, locale }: PriceLadderProps) {
  const gradientId = useId()
  const geom = rulerGeometry(
    { min: distribution.lowest, max: distribution.highest },
    WIDTH,
    PAD,
  )
  const x = (value: number) => rulerX(geom, value, dir)

  const ticks: Tick[] = [
    { key: 'low', value: distribution.lowest, label: priceBriefT('ladderLow', locale), major: false },
    { key: 'p25', value: distribution.p25, label: priceBriefT('ladderP25', locale), major: false },
    { key: 'median', value: distribution.median, label: priceBriefT('ladderMedian', locale), major: true },
    { key: 'p75', value: distribution.p75, label: priceBriefT('ladderP75', locale), major: false },
    { key: 'high', value: distribution.highest, label: priceBriefT('ladderHigh', locale), major: false },
  ]

  const markerX = price != null ? x(price) : null
  const medianX = x(distribution.median)

  return (
    <svg
      viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
      role="img"
      data-testid="price-ladder"
      data-dir={dir}
      aria-label={
        price != null
          ? `${priceBriefT('ladderMedian', locale)} ${formatMoney(distribution.median, currency)} — ` +
            `${priceBriefT('theirPrice', locale)} ${formatMoney(price, currency)}`
          : `${priceBriefT('ladderMedian', locale)} ${formatMoney(distribution.median, currency)}`
      }
      className="block w-full"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0" stopColor="var(--lc-surface-sunken)" />
          <stop offset="0.5" stopColor="var(--lc-action-secondary)" />
          <stop offset="1" stopColor="var(--lc-surface-sunken)" />
        </linearGradient>
      </defs>

      {/* Outer band: observed lowest→highest range. */}
      <rect
        x={Math.min(x(distribution.lowest), x(distribution.highest))}
        y={BAND_Y}
        width={Math.abs(x(distribution.highest) - x(distribution.lowest))}
        height={BAND_H}
        rx={4}
        fill={`url(#${gradientId})`}
        stroke="var(--lc-border)"
        strokeWidth={1}
        data-testid="ladder-band-range"
      />
      {/* Inner band: the p25→p75 "priced around" region. */}
      <rect
        x={Math.min(x(distribution.p25), x(distribution.p75))}
        y={BAND_Y + 3}
        width={Math.abs(x(distribution.p75) - x(distribution.p25))}
        height={BAND_H - 6}
        rx={3}
        fill="var(--lc-accent)"
        data-testid="ladder-band-iqr"
      />

      {/* Ticks; the median is the visual anchor (boldest). */}
      {ticks.map((tick) => {
        const tickX = x(tick.value)
        return (
          <g key={tick.key} data-testid={`ladder-tick-${tick.key}`}>
            <line
              x1={tickX}
              y1={BAND_Y - (tick.major ? 10 : 6)}
              x2={tickX}
              y2={BAND_Y + BAND_H + (tick.major ? 10 : 6)}
              stroke={tick.major ? 'var(--lc-text-heading)' : 'var(--lc-border-strong)'}
              strokeWidth={tick.major ? 2.5 : 1.5}
            />
            <text
              x={tickX}
              y={BAND_Y + BAND_H + 26}
              textAnchor="middle"
              fontSize={tick.major ? 13 : 12}
              fontWeight={tick.major ? 700 : 500}
              fill={tick.major ? 'var(--lc-text-heading)' : 'var(--lc-text-muted)'}
              fontFamily="var(--lc-font-ui)"
            >
              {tick.label}
            </text>
            <text
              x={tickX}
              y={BAND_Y + BAND_H + 40}
              textAnchor="middle"
              fontSize={11}
              fill="var(--lc-text-muted)"
              fontFamily="var(--lc-font-mono)"
            >
              {formatMoney(tick.value, currency)}
            </text>
          </g>
        )
      })}

      {/* Asking-price marker: leader line + arrowhead + label. Absent when no price. */}
      {markerX != null && (
        <g data-testid="ladder-marker" data-price={price}>
          <line
            x1={markerX}
            y1={16}
            x2={markerX}
            y2={BAND_Y - 4}
            stroke="var(--lc-action-primary)"
            strokeWidth={2}
            strokeDasharray="3 3"
          />
          <path
            d={`M ${markerX - 6} 22 L ${markerX + 6} 22 L ${markerX} 12 Z`}
            fill="var(--lc-action-primary)"
            transform={dir === 'rtl' ? `rotate(180 ${markerX} 17)` : undefined}
          />
          <circle cx={markerX} cy={BAND_Y + BAND_H / 2} r={5.5} fill="var(--lc-action-primary)" />
          <text
            x={markerX}
            y={HEIGHT - 6}
            textAnchor={markerX > WIDTH - 80 ? 'end' : markerX < 80 ? 'start' : 'middle'}
            fontSize={12}
            fontWeight={600}
            fill="var(--lc-action-primary)"
            fontFamily="var(--lc-font-ui)"
          >
            {priceBriefT('theirPrice', locale)} · {formatMoney(price, currency)}
          </text>
        </g>
      )}

      {/* Median anchor line on top so it stays visible under the marker. */}
      <line
        x1={medianX}
        y1={BAND_Y - 12}
        x2={medianX}
        y2={BAND_Y + BAND_H + 12}
        stroke="var(--lc-text-heading)"
        strokeWidth={2.5}
        data-testid="ladder-median-line"
      />
    </svg>
  )
}
